import { environment } from "@app/relay/relay-environment";
import clsx from "clsx";
import { useState } from "react";
import { commitMutation, graphql } from "react-relay";

import { FunnelScreen, PrimaryButton } from "../screen";
import { sourceFor } from "../sources";
import StatementUploader from "../statement-uploader";

import type { sourceScreen_StripeConnectMutation } from "@app/queries/sourceScreen_StripeConnectMutation.graphql";
import type { Lead, UploadedFile } from "../types";

const stripeKeyConnectMutation = graphql`
  mutation sourceScreen_StripeConnectMutation(
    $token: String
    $apiKey: String!
  ) {
    leadStripeConnect(token: $token, apiKey: $apiKey) {
      lead {
        token
      }
      errors {
        message
      }
    }
  }
`;

// The two we have OAuth apps registered for. Everything else with an `oauth`
// route in the table is a queue entry, not a button that works.
const PROVIDER: Record<string, string> = {
  QUICKBOOKS: "qbo",
  STRIPE: "stripe",
};

// One system, and every way into it that system actually has. What is on this
// screen is decided by sources.ts rather than by a shape shared across all
// twenty: QuickBooks has no key to paste because Intuit issues none, Wise has
// no connection because its API is not the route we built, and a bank has
// neither because no US bank opens itself to us without an aggregator.
export default function SourceScreen({
  id,
  name,
  lead,
  token,
  busy,
  onBack,
  onConnected,
  onNoData,
  reload,
}: {
  id: string;
  name: string;
  lead: Lead;
  token: string | null;
  busy: boolean;
  onBack: () => void;
  onConnected: () => void;
  onNoData: () => void;
  reload: () => Promise<void>;
}) {
  const source = sourceFor(id);
  const integration = lead.integrations.find(
    (i) => i.provider === PROVIDER[id]
  );
  const files = lead.uploadedFiles.filter((f) => f.sourceId === id);
  const connected = integration?.connected ?? false;

  return (
    <FunnelScreen title={name}>
      <button
        type="button"
        onClick={onBack}
        className="mt-4 text-caption font-normal text-zinc-500 transition-colors hover:text-zinc-700"
      >
        ← All your software
      </button>

      {source.note && (
        <p className="mt-4 text-body text-zinc-600">{source.note}</p>
      )}

      <SyncStatus
        name={name}
        connected={connected}
        displayName={integration?.displayName}
        files={files}
      />

      {source.oauth && (
        <Route
          title={`Connect ${name}`}
          hint="Read-only, and revocable from your side at any time."
          state={
            source.oauth === "live" &&
            integration?.available &&
            integration.connectUrl
              ? "live"
              : "planned"
          }
        >
          {integration?.connectUrl && (
            <a
              href={integration.connectUrl}
              className="flex h-11 w-full items-center justify-center rounded-lg bg-blue-600 text-body font-semibold text-white transition-colors hover:bg-blue-700"
            >
              {connected ? `Reconnect ${name}` : `Connect ${name}`}
            </a>
          )}
        </Route>
      )}

      {source.apiKey && (
        <Route
          title="Paste a read-only key"
          hint={source.keyHint}
          state={source.apiKey === "live" ? "live" : "planned"}
        >
          {id === "STRIPE" && (
            <StripeKeyForm
              busy={busy}
              token={token}
              onConnected={onConnected}
              reload={reload}
            />
          )}
        </Route>
      )}

      {source.csv && (
        <Route title="Upload an export" hint={source.csvHint} state="live">
          <StatementUploader
            sourceId={id}
            token={token}
            label={name}
            files={files}
            onUploaded={async () => {
              await reload();
              onConnected();
            }}
            onDelete={() => {}}
          />
        </Route>
      )}

      <p className="mt-6 text-center">
        <button
          type="button"
          onClick={() => !busy && onNoData()}
          className="text-body text-zinc-500 underline decoration-zinc-300 underline-offset-4 transition-colors hover:text-zinc-700 hover:decoration-zinc-500"
        >
          I do not have any data available for this software
        </button>
      </p>
    </FunnelScreen>
  );
}

// One way in. A planned route is shown and cannot be taken — the point of it on
// the screen is that a lead can see we know the route exists, which a missing
// button cannot say.
function Route({
  title,
  hint,
  state,
  children,
}: {
  title: string;
  hint?: string;
  state: "live" | "planned";
  children?: React.ReactNode;
}) {
  const planned = state === "planned";

  return (
    <div
      className={clsx(
        "mt-5 rounded-xl border p-4",
        planned ? "border-dashed border-zinc-200" : "border-zinc-200"
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <p
          className={clsx(
            "text-caption",
            planned ? "text-zinc-400" : "text-zinc-700"
          )}
        >
          {title}
        </p>
        {planned && (
          <span className="shrink-0 rounded-full bg-zinc-100 px-2 py-0.5 text-micro text-zinc-500">
            Coming soon
          </span>
        )}
      </div>

      {hint && (
        <p
          className={clsx(
            "mt-1 text-caption font-normal",
            planned ? "text-zinc-400" : "text-zinc-500"
          )}
        >
          {hint}
        </p>
      )}

      {!planned && children && <div className="mt-3">{children}</div>}
    </div>
  );
}

// What we can see of this source right now. For a connection that is the
// connection; for an export it is what the parser made of the file, which is
// the most convincing thing on the screen when it worked and the most useful
// when it did not.
function SyncStatus({
  name,
  connected,
  displayName,
  files,
}: {
  name: string;
  connected: boolean;
  displayName?: string | null;
  files: readonly UploadedFile[];
}) {
  const lines: string[] = [];

  if (connected) {
    lines.push(displayName ? `Connected — ${displayName}` : "Connected");
    lines.push(`We sync ${name} on every run from here on.`);
  }

  for (const file of files) {
    if (file.parseState === "PARSED")
      lines.push(
        `${file.filename} — read ${
          file.txnCount?.toLocaleString() ?? "0"
        } transactions` +
          (file.coverageStart && file.coverageEnd
            ? `, ${file.coverageStart} to ${file.coverageEnd}`
            : "")
      );
    else if (file.parseState === "FAILED")
      lines.push(
        `${file.filename} — couldn't be read${
          file.errorMessage ? `: ${file.errorMessage}` : ""
        }`
      );
    else lines.push(`${file.filename} — reading it now…`);
  }

  if (lines.length === 0) return null;

  const bad = files.some((f) => f.parseState === "FAILED");

  return (
    <div
      className={clsx(
        "mt-4 rounded-xl border p-3",
        bad ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"
      )}
    >
      {lines.map((line) => (
        <p
          key={line}
          className={clsx(
            "text-caption font-normal",
            bad ? "text-red-700" : "text-emerald-800"
          )}
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function StripeKeyForm({
  busy,
  token,
  onConnected,
  reload,
}: {
  busy: boolean;
  token: string | null;
  onConnected: () => void;
  reload: () => Promise<void>;
}) {
  const [key, setKey] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!key.trim() || connecting || busy) return;
    setConnecting(true);
    setError(null);
    commitMutation<sourceScreen_StripeConnectMutation>(environment, {
      mutation: stripeKeyConnectMutation,
      variables: { token, apiKey: key.trim() },
      onCompleted: async (response) => {
        setConnecting(false);
        const message = response.leadStripeConnect?.errors?.[0]?.message;
        if (message) {
          setError(message);
          return;
        }
        await reload();
        onConnected();
      },
      onError: () => {
        setConnecting(false);
        setError("Something went wrong. Please try again.");
      },
    });
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label htmlFor="stripe-key" className="sr-only">
        Stripe restricted key
      </label>
      <input
        id="stripe-key"
        name="stripe-key"
        type="text"
        value={key}
        placeholder="rk_live_…"
        onChange={(event) => setKey(event.target.value)}
        className="h-11 w-full rounded-lg border border-zinc-300 px-3 font-mono text-body text-zinc-900 transition-colors placeholder:text-zinc-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20"
      />
      {error && (
        <p className="mt-1.5 text-caption font-normal text-red-600">{error}</p>
      )}
      <div className="mt-3">
        <PrimaryButton type="submit" disabled={connecting || busy}>
          {connecting ? "Checking the key…" : "Connect with this key"}
        </PrimaryButton>
      </div>
    </form>
  );
}
